import React from 'react'
import { Button } from '@mui/material'
import { useFormik } from 'formik'
import Field from './Field.tsx'
import ErrorBoundary from '../../misc/ErrorBoundary.jsx'
import { compareAsc, format, isValid } from 'date-fns'
import { context } from '../../index.js'
import { Icons } from '../../misc/Icons.tsx'
import { evaluateDateString } from '../../misc/dateValidation.ts'
import { IField } from '../../interfaces.ts'


const getFieldInitialValue = (field, initialValues) => {
    if (field.name in initialValues) {
        return initialValues[field.name]
    }

    if (field.guess) {
        return field.guess
    } else if (field.type === "checkbox") {
        return "false"
    } else if (field.type === "fixed-value") {
        return field.value
    } else if (field.type === "table") {
        return []
    } else {
        return ""
    }
}


const validateForm = (fields: IField[], values: any) => {
    var errors = {}

    fields = fields.filter(field => !!field.name && !context.isHidden(field, values))
    console.log("Validating fields", fields.map(field => field.name))

    fields.forEach(field => {
        try {

            // Handle any null or empty fields first
            if ((values[field.name] === "" || values[field.name] === null || values[field.name] === undefined)) {
                if (field.required !== false) {
                    errors[field.name] = "This field is required"
                }
                return
            }

            if (field.type === "table") {
                if (!Array.isArray(values[field.name])) {
                    errors[field.name] = "Must be an array"
                    return
                }
                if (typeof field.table?.min_rows === "number" && values[field.name].length < field.table?.min_rows) {
                    errors[field.name] = `You must have at least ${field.table?.min_rows} entries`
                }
                if (typeof field.table?.max_rows === "number" && values[field.name].length > field.table?.max_rows) {
                    errors[field.name] = `You are limited to ${field.table?.min_rows} entries`
                }
                return
            }

            if (field.type === "date") {
                var date = new Date(values[field.name])

                if (!isValid(date)) {
                    errors[field.name] = "Enter a valid date"
                    return
                }

                if (field.min) {
                    var minDate = evaluateDateString({...context.answers, ...values}, field.min)
                    if (minDate && compareAsc(date, minDate) < 0) {
                        errors[field.name] = `Enter a date after ${format(minDate, "do LLLL u")}`
                        return
                    }
                }

                if (field.max) {
                    var maxDate = evaluateDateString({...context.answers, ...values}, field.max)
                    if (maxDate && compareAsc(date, maxDate) > 0) {
                        errors[field.name] = `Enter a date before ${format(maxDate, "do LLLL u")}`
                        return
                    }
                }
                return
            }
            if (field.type === "phone-number") {
                
                if (typeof values[field.name] !== "string") {
                    errors[field.name] = "Must be string"
                    return
                }
                
                // eslint-disable-next-line no-invalid-regexp
                const phoneRegex = new RegExp("^\\+[0-9][0-9- ]{7,20}[0-9]$")
                var phoneNumberRegexResult = phoneRegex.test(values[field.name])
                if (phoneNumberRegexResult === false) {
                    errors[field.name] = "Enter a valid phone number"
                }
                return
            }
            if (field.name) {
                
                if (typeof values[field.name] !== "string") {
                    errors[field.name] = "Must be string"
                    return
                }

                var regex = field.regex ? new RegExp(field.regex) : new RegExp("^[“”‘’ -~À-Ö\\p{Sc}\\p{So}\\p{Mn}\\p{P}\\p{Z}À-ÿ\\w\\n]*$")
                var regexResult = regex.test(values[field.name])
                if (regexResult === false) {
                    errors[field.name] = field.regexMessage ? field.regexMessage : "Only use standard European characters"
                    return
                }

                if (field.min && values[field.name].length < field.min) {
                    errors[field.name] = `You must enter at least ${field.min} characters`
                    return
                }
                if (field.max && values[field.name].length > field.max) {
                    errors[field.name] = `You are limited to ${field.max} characters`
                    return
                }
                return
            }
        } catch (e) {
            console.log("Failed to validate", field.name, {...field}, e)
        }
    })
    
    console.log("Errors", errors)
    return errors
}


export default function FormFields ({
    fields,
    initialValues = {},
    onChange = (formik) => {},
    onSubmit = (values) => {},
    onCancel = null,
    submitCount = 0,
    maxHeight = "none",
    submitButtonText = "Submit",
    quitButtonText = ""
}) {

    // Set intial value to guess
    fields.filter(field => !!field.name && !context.isHidden(field)).forEach(field => {
        initialValues[field.name] = getFieldInitialValue(field, initialValues)
    })

    var formik = useFormik({

        initialValues: initialValues,
        // validationSchema: (values) => getValidationSchema(fields, values),
        validate: (values) => validateForm(fields, values),
        validateOnMount: true,
        enableReinitialize: true,

        onSubmit: (values) => {
            return onSubmit(values)
        }
    })

    const applyConditionsToFormik = () => {

        var displayedFieldKeys = []

        fields.forEach(field => {
            if (!context.isHidden(field, formik.values)) {
                displayedFieldKeys.push(field.name)
            }
        })

        fields.forEach(field => {
            if (!displayedFieldKeys.includes(field.name)) {
                delete formik.values[field.name]
                delete formik.errors[field.name]
            }
            // This is for when fields which are initially hidden and then become shown, 
            // the correct initial value needs to be reassigned.
            if (displayedFieldKeys.includes(field.name) && !Object.keys(formik.values).includes(field.name)) {
                formik.values[field.name] = getFieldInitialValue(field, initialValues)
            }
        })
    }

    React.useEffect(() => {
        applyConditionsToFormik()
    })

    React.useEffect(() => {
        onChange(formik)
    }, [formik.values, formik.errors, formik, onChange])

    return (
        <form className="form" onSubmit={formik.handleSubmit}>
            <div className="form-fields" style={{maxHeight: maxHeight}}>
                {fields.map((field, i) => {

                    var valuesToEvaluateConditions = {}
                    fields.forEach(field => {
                        if (field.name in formik.values && formik.values[field.name] !== "") {
                            valuesToEvaluateConditions[field.name] = formik.values[field.name]
                        } else {
                            valuesToEvaluateConditions[field.name] = undefined
                        }
                    })

                    if (context.isHidden(field, valuesToEvaluateConditions) || field.type === "fixed-value") {
                        return <React.Fragment key={`${i}-${field.name}`}></React.Fragment>
                    }

                    return (
                        <ErrorBoundary key={`${i}-${field.name}`}>
                            <div>
                                <Field
                                    field={field}
                                    formik={formik}
                                    autoFocus={i === 0} />

                                <span className="warning">
                                    {(submitCount || formik.submitCount) && formik.errors[field.name] ? formik.errors[field.name] : null}
                                </span>
                            </div>
                        </ErrorBoundary>
                    )
                })}
            </div>


            {onCancel || submitButtonText ? (
                <div className="form-buttons" style={{marginTop: "30px"}}>
                    {onCancel ? (
                        <Button
                            onClick={() => onCancel()}
                            className="btn btn-lg"
                            variant="contained"
                            color="info"
                            disabled={formik.isSubmitting || context.locked}>
                            <Icons.ArrowLeft /> Cancel
                        </Button>
                    ) : null}

                    {quitButtonText ? (
                        <Button
                            color="info"
                            variant="contained"
                            className="btn btn-lg"
                            onClick={() => context.saveAndQuit()}>
                            {quitButtonText}
                        </Button>
                    ) : null}

                    {submitButtonText ? (
                        <Button
                            type="submit"
                            color="success"
                            variant="contained"
                            className="btn btn-lg">
                            {submitButtonText}
                        </Button>
                    ) : null}
                </div>
            ) : null}
        </form>
    )
}